import React from "react";
import {SnackbarShowMessage, useSnackbar} from "../../context/snackbar-context";

export interface WithSnackbarProps {
  snackbarShowMessage: SnackbarShowMessage;
}

/**
 * Higher-order component that provides a snackbar message functionality to the wrapped component.
 *
 * The wrapped component will receive a prop called `snackbarShowMessage`, which is a function that can be used to display a snackbar message with the specified severity and duration.
 * @param WrappedComponent - The component to be wrapped by the withSnackbar HOC.
 * @returns A new component that renders the WrappedComponent with {@link SnackbarShowMessage} functionality and a Snackbar component for displaying messages.
 *
 * @see {@link SnackbarShowMessage} for the type definition of the snackbarShowMessage function.
 *
 * @example
 * const MyComponent = ({ snackbarShowMessage }) => {
 *   const handleClick = () => {
 *     snackbarShowMessage("This is a success message!", "success", 3000);
 *   };
 *
 *   return <button onClick={handleClick}>Show Snackbar</button>;
 * };
 *
 * export default withSnackbar(MyComponent);
 *
 * Compatibility shim — delegates to {@link useSnackbar} from SnackbarProvider.
 *
 * @deprecated Existing consumers wrapped with `withSnackbar(Component)` continue to work
 * without changes. New components should call {@link useSnackbar} directly instead.
 *
 * @see {@link useSnackbar}
 */
export function withSnackbar<P extends WithSnackbarProps>(
  WrappedComponent: React.ComponentType<P>
): React.FC<Omit<P, keyof WithSnackbarProps>> {
  const WithSnackbar = (props: Omit<P, keyof WithSnackbarProps>) => {
    const { snackbarShowMessage } = useSnackbar();
    return <WrappedComponent {...(props as P)} snackbarShowMessage={snackbarShowMessage} />;
  };

  WithSnackbar.displayName = `withSnackbar(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithSnackbar;
}
